import { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { routes } from '@/config/routes';
import { useAuth } from './AuthProvider';
import { isPinUnlocked, lockPin, pinUnlockRemainingMs } from './pinGate';
import { PinGateLanding } from './PinGateLanding';

/**
 * Paths the soft-launch PIN must never cover.
 *
 * `/auth/callback` is the Passport SSO return. `/auth/hop` starts PKCE on this
 * origin so a known Passport session can land already signed in. Both arrive
 * mid-round-trip; a keypad over either burns the one-time code.
 */
const PIN_EXEMPT_PATHS: readonly string[] = [routes.authCallback, routes.authHop];

/**
 * Site-wide soft launch PIN. Renders gate until unlocked.
 * Unlock is valid for 24 hours (same as HOTTR.tv).
 * A signed-in Passport session skips the keypad — the PIN is per-origin and
 * would otherwise ask again on every world hop.
 */
export function RequirePin({ children }: { children: React.ReactNode }) {
  const { pathname } = useLocation();
  const { user, loading } = useAuth();
  const exempt = PIN_EXEMPT_PATHS.includes(pathname);
  const authenticated = loading ? undefined : Boolean(user);
  const [ok, setOk] = useState(() => isPinUnlocked());
  const onUnlocked = useCallback(() => setOk(true), []);

  useEffect(() => {
    if (authenticated || !ok) return;

    const recheck = () => {
      if (!isPinUnlocked()) {
        lockPin();
        setOk(false);
      }
    };

    const remaining = pinUnlockRemainingMs();
    const expiryTimer =
      remaining > 0
        ? window.setTimeout(() => {
            lockPin();
            setOk(false);
          }, remaining + 250)
        : null;

    const interval = window.setInterval(recheck, 60_000);
    const onVisible = () => {
      if (document.visibilityState === 'visible') recheck();
    };

    window.addEventListener('focus', recheck);
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      if (expiryTimer != null) window.clearTimeout(expiryTimer);
      window.clearInterval(interval);
      window.removeEventListener('focus', recheck);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [ok, authenticated]);

  if (exempt || authenticated) {
    return <>{children}</>;
  }

  // Hold rather than flash a keypad at someone who turns out to be signed in.
  if (authenticated === undefined) {
    return null;
  }

  if (!ok) {
    return <PinGateLanding onUnlocked={onUnlocked} />;
  }

  return <>{children}</>;
}
